/**
 * Extracts clean word sequences from HTML text, finding multiple sequences of consecutive words
 * that don't contain HTML tags or punctuation.
 * @param {string} html - The HTML string to process
 * @param {number} wordCount - Number of consecutive words in each sequence (default: 5)
 * @param {number} maxSequences - Maximum number of sequences to return (default: 10)
 * @returns {string[]} Array of cleaned word sequences
 */
export const extractCleanSequences = (html, wordCount = 5, maxSequences = 10) => {
  if (!html) return [];

  try {
    const sequences = [];
    let currentSequence = [];
    let currentWord = '';
    let inTag = false;
    
    // Process the string character by character
    for (let i = 0; i < html.length; i++) {
      const char = html[i];
      
      // Handle HTML tags
      if (char === '<') {
        inTag = true;
        // If we were building a word, add it to sequence if valid
        if (currentWord) {
          if (/^[\w'’]+$/.test(currentWord)) {
            currentSequence.push(currentWord);
            // Check if we have a complete sequence
            if (currentSequence.length >= wordCount) {
              sequences.push(currentSequence.slice(0, wordCount).join(' '));
              currentSequence = [];
              if (sequences.length >= maxSequences) {
                return sequences;
              }
            }
          } else {
            // Invalid word, reset sequence
            currentSequence = [];
          }
          currentWord = '';
        }
        continue;
      } else if (char === '>' && inTag) {
        inTag = false;
        currentSequence = []; // Reset sequence after a tag
        currentWord = '';
        continue;
      }
      
      // Skip processing if inside a tag
      if (inTag) continue;
      
      // Handle word boundaries
      if (/\s/.test(char)) {
        if (currentWord) {
          if (/^[\w'’]+$/.test(currentWord)) {
            currentSequence.push(currentWord);
            // Check if we have a complete sequence
            if (currentSequence.length >= wordCount) {
              sequences.push(currentSequence.slice(0, wordCount).join(' '));
              currentSequence = [];
              if (sequences.length >= maxSequences) {
                return sequences;
              }
            }
          } else {
            // Invalid word, reset sequence
            currentSequence = [];
          }
          currentWord = '';
        }
      } else if (/[^\w'’]/.test(char)) {
        // Handle punctuation - reset sequence if we encounter any non-word character
        if (currentWord) {
          if (/^[\w'’]+$/.test(currentWord)) {
            currentSequence.push(currentWord);
            if (currentSequence.length >= wordCount) {
              sequences.push(currentSequence.slice(0, wordCount).join(' '));
              currentSequence = [];
              if (sequences.length >= maxSequences) {
                return sequences;
              }
            }
          } else {
            currentSequence = [];
          }
          currentWord = '';
        }
        currentSequence = []; // Reset sequence on any punctuation
      } else {
        // Add character to current word
        currentWord += char.toLowerCase();
      }
    }
    
    // Process any remaining word at the end
    if (currentWord && /^[\w'’]+$/.test(currentWord)) {
      currentSequence.push(currentWord);
      if (currentSequence.length >= wordCount) {
        sequences.push(currentSequence.slice(0, wordCount).join(' '));
      }
    }
    
    return sequences.length > 0 ? sequences : ['No readable sequences found'];
  } catch (error) {
    console.error('Error processing citation content:', error);
    return ['Error processing content'];
  }
};

/**
 * Safely extracts text from HTML content
 * @param {string} html - The HTML string to process
 * @returns {string} Cleaned text without HTML tags
 */
export const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
};
